/**
 * Singleton fee-payer resolver. Replaces call-site `useRelayConfig`.
 *
 * Relocated from frontend/src/lib/tx-engine/relay/fee-payer.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E).
 *
 * The frontend version called the v1 `@morfi/backend-sdk` `getRelayConfig`.
 * The v2 SDK MUST NOT depend on v1 — the relay config route
 * (`GET /v1/config/relay`, a public endpoint) is fetched directly via the
 * universal `fetch`; the SDK's installed fetch shim rewrites the relative
 * path against the API base URL.
 *
 * Platform-neutral: `fetch` is universal. Safe in src/auth/ core.
 */

import type { TxEngineLogger } from '../config.js';

export interface RelayConfig {
  feePayerPublicKey: string;
  priorityFeeMicroLamports: number;
}

let cachedRelayConfigPromise: Promise<RelayConfig> | null = null;

/** Backend `{ data, requestId }` envelope for the relay config route. */
interface RelayConfigEnvelope {
  data?: {
    feePayerPublicKey?: string;
    priorityFeeMicroLamports?: number;
  };
}

async function fetchRelayConfig(logger: TxEngineLogger): Promise<RelayConfig> {
  const response = await fetch('/v1/config/relay', {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Relay config HTTP ${response.status}`);
  }
  const envelope = (await response.json()) as RelayConfigEnvelope;
  const feePayer = envelope.data?.feePayerPublicKey;
  if (!feePayer) {
    throw new Error('Relay config missing feePayerPublicKey');
  }

  const configuredPriorityFee = envelope.data?.priorityFeeMicroLamports;
  const priorityFeeMicroLamports =
    typeof configuredPriorityFee === 'number' &&
    Number.isSafeInteger(configuredPriorityFee) &&
    configuredPriorityFee >= 0
      ? configuredPriorityFee
      : 0;
  if (priorityFeeMicroLamports !== configuredPriorityFee) {
    logger.warn('tx.relay_priority_fee.config_fallback', { fallbackMicroLamports: '0' });
  }

  return { feePayerPublicKey: feePayer, priorityFeeMicroLamports };
}

/**
 * Resolves the relay fee policy once per session. Concurrent calls
 * share the same in-flight promise. On rejection the cache busts so a
 * subsequent call can retry. Per data-model.md::RelayFeePayerCache.
 */
export async function resolveRelayConfig(logger: TxEngineLogger): Promise<RelayConfig> {
  if (cachedRelayConfigPromise === null) {
    cachedRelayConfigPromise = fetchRelayConfig(logger);
    cachedRelayConfigPromise.catch(() => {
      cachedRelayConfigPromise = null;
    });
  }
  return cachedRelayConfigPromise;
}

export async function resolveRelayFeePayer(logger: TxEngineLogger): Promise<string> {
  return (await resolveRelayConfig(logger)).feePayerPublicKey;
}

/**
 * Clear the fee-payer cache. Called from the network-toggle path so a user
 * flipping devnet ↔ mainnet picks up the new-network's relay fee-payer on
 * the next submission.
 */
export function clearRelayFeePayerCache(): void {
  cachedRelayConfigPromise = null;
}
